/**
 * Painel de administração: usuários e catálogo.
 *
 * ## O 403 é uma tela, não um erro
 *
 * Quem não é administrador chega aqui — a rota é pública no roteador, e o
 * pacote do front é o mesmo para todo mundo. Quem recusa é o servidor. Então
 * a recusa precisa **ler como uma resposta**, não como falha: quem deveria ter
 * acesso descobre que precisa pedir, em vez de achar que a plataforma quebrou.
 *
 * ## Por que o catálogo edita só parte dos campos
 *
 * O padrão de acesso não aparece no formulário do item: ele decide a natureza
 * da pergunta "quem entra", e é cadastro, não apresentação. O motivo completo
 * está em `backend/src/db/catalog.js`.
 *
 * Os grupos do Entra têm **aba própria** desde 19/10. Também concedem acesso —
 * mais que qualquer outro campo —, e justamente por isso não dividem
 * formulário com nome e ícone: lá cada vínculo mostra quantas pessoas libera
 * antes de confirmar, e fica no log de auditoria.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, photoUrl, logoUrl, ApiError, SessionError, goToSignIn } from '../api.js';
import { AppShell, Banner, StatsBar } from '../components/AppShell.jsx';
import { Icon } from '../components/Icon.jsx';
import { Panel } from '../components/Panel.jsx';
import { Avatar } from '../components/Avatar.jsx';
import { usePageMeta } from '../hooks/usePageMeta.js';
import { relativeDays } from '../time.js';
import { notify, Toast } from '../components/Toast.jsx';
import { AdminConversations } from '../components/AdminConversations.jsx';
import { AdminAccess } from '../components/AdminAccess.jsx';
import { AdminRequests } from '../components/AdminRequests.jsx';
import { NewItem } from '../components/NewItem.jsx';
import { AdminFeedback } from '../components/AdminFeedback.jsx';
import { AdminSites } from '../components/AdminSites.jsx';
import { AdminGrupos } from '../components/AdminGrupos.jsx';
import { AdminAbas } from '../components/AdminAbas.jsx';
import { GruposDaPessoa } from '../components/DiretorioPaineis.jsx';
import { AreaPicker } from '../components/AreaPicker.jsx';

/**
 * As abas, em três grupos.
 *
 * A ordem antiga era de acréscimo — cada aba nova entrava onde coubesse — e
 * oito rótulos iguais em fila não dizem que Acessos e Grupos tratam do mesmo
 * assunto enquanto Conversas trata de outro. Os grupos são:
 *
 *   1. **Quem alcança o quê.** Começa por Solicitações porque é a única que
 *      ESPERA alguém: as outras três são consulta, esta é fila.
 *   2. **O que a plataforma oferece.** Catálogo é o que a casa publica,
 *      Artefatos é o que as pessoas publicam, e Abas é a divisão em que as
 *      duas coisas se mostram — por último porque é a mais raramente mexida.
 *   3. **O que as pessoas disseram.** Mensagens pede resposta, Conversas é
 *      observação — por isso nessa ordem.
 *
 * `separa` desenha a divisória ANTES da aba, e é decoração: o agrupamento já
 * está na ordem, e anunciá-lo a um leitor de tela não acrescentaria nada.
 */
const TABS = [
  { id: 'requests', label: 'Solicitações', icon: 'inbox' },
  { id: 'users', label: 'Usuários', icon: 'user' },
  { id: 'access', label: 'Acessos', icon: 'key' },
  { id: 'grupos', label: 'Grupos', icon: 'users' },

  { id: 'catalog', label: 'Catálogo', icon: 'package', separa: true },
  { id: 'sites', label: 'Artefatos', icon: 'window' },
  { id: 'abas', label: 'Abas', icon: 'grid' },

  { id: 'feedback', label: 'Mensagens', icon: 'mail', separa: true },
  { id: 'conversations', label: 'Conversas', icon: 'chat' },
];

/** Data e hora curtas. `null` vira travessão, nunca "Invalid Date". */
function dateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ── usuários ────────────────────────────────────────────────── */

function UsersTab({ me, items }) {
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [vendoGrupos, setVendoGrupos] = useState(null);
  const [search, setSearch] = useState('');
  const [only, setOnly] = useState('ALL');

  const [online, setOnline] = useState({ quantos: 0, janelaMin: 3 });

  /**
   * `silencioso` existe por causa da recarga automática.
   *
   * Sem ele, a cada 30 segundos a tabela sumiria e voltaria com "Carregando…"
   * debaixo do cursor de quem está lendo uma linha. A primeira carga mostra o
   * estado; as seguintes trocam os dados por baixo.
   */
  const load = useCallback(async (silencioso = false) => {
    if (!silencioso) setLoading(true);
    setError(null);
    try {
      const r = await api.admin.users();
      setUsers(r.users);
      setOnline({ quantos: r.onlineCount ?? 0, janelaMin: r.janelaOnlineMin ?? 3 });
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      // Numa recarga automática o erro não vai para a tela: a lista que já
      // está desenhada continua valendo, e um aviso vermelho piscando sozinho
      // a cada 30 segundos seria pior do que o dado envelhecer em silêncio.
      if (!silencioso) setError(err.message);
    } finally {
      if (!silencioso) setLoading(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Quem está online muda sozinho, então a tela se atualiza sozinha.
   *
   * 30 segundos é a mesma cadência com que o sino bate no servidor — é ela que
   * define a resolução possível do dado, e consultar mais rápido não traria
   * informação nova, só requisição.
   *
   * `document.hidden`: a aba de administração esquecida em segundo plano não
   * deveria consultar o servidor para sempre. É a mesma regra do sino.
   */
  useEffect(() => {
    const timer = setInterval(() => {
      if (!document.hidden) load(true);
    }, 30_000);

    const aoVoltar = () => {
      if (!document.hidden) load(true);
    };
    document.addEventListener('visibilitychange', aoVoltar);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [load]);

  async function toggleAdmin(user) {
    setSaving(user.oid);
    try {
      const r = await api.admin.setAdmin(user.oid, !user.isAdmin);
      // Troca só a linha alterada. Recarregar a lista inteira reordenaria por
      // `last_seen_at` e a linha saltaria de lugar debaixo do cursor.
      setUsers((list) => list.map((u) => (u.oid === r.user.oid ? { ...u, ...r.user } : u)));
      notify(
        r.user.isAdmin
          ? `${r.user.displayName ?? r.user.upn} agora é administrador.`
          : `${r.user.displayName ?? r.user.upn} deixou de ser administrador.`,
      );
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSaving(null);
    }
    return undefined;
  }

  const admins = users?.filter((u) => u.isAdmin).length ?? 0;

  const visible = useMemo(() => {
    if (!users) return [];
    const term = search.trim().toLowerCase();
    return users.filter((u) => {
      if (only === 'ADMIN' && !u.isAdmin) return false;
      if (!term) return true;
      return (
        (u.displayName ?? '').toLowerCase().includes(term) || u.upn.toLowerCase().includes(term)
      );
    });
  }, [users, search, only]);

  return (
    <>
      <StatsBar
        items={[
          { label: 'Pessoas que já entraram', value: users?.length ?? '—' },
          { label: 'Administradores', value: users ? admins : '—' },
          // O número que muda sozinho. Fica ao lado dos outros dois de
          // propósito: é um número sobre as mesmas pessoas, não um painel à
          // parte.
          {
            label: `Usando agora (últimos ${online.janelaMin} min)`,
            value: users ? online.quantos : '—',
          },
        ]}
      />

      <div className="list-panel">
        <div className="tabs-row">
          <div className="tabs" role="tablist" aria-label="Filtrar usuários">
            {[
              { id: 'ALL', label: 'Todos' },
              { id: 'ADMIN', label: 'Administradores' },
            ].map((o) => (
              <button
                key={o.id}
                type="button"
                role="tab"
                aria-selected={only === o.id}
                className={`tab ${only === o.id ? 'is-active' : ''}`}
                onClick={() => setOnly(o.id)}
              >
                {o.label}
              </button>
            ))}
          </div>

          <input
            className="search-box"
            type="search"
            placeholder="Buscar por nome ou e-mail…"
            aria-label="Buscar usuário"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading && <p className="state-message">Carregando…</p>}

        {error && (
          <div className="state-message error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={load}>
              Tentar de novo
            </button>
          </div>
        )}

        {!loading && !error && users?.length === 0 && (
          <p className="state-message">Ninguém entrou na plataforma ainda.</p>
        )}

        {!loading && !error && users?.length > 0 && visible.length === 0 && (
          <p className="state-message">Ninguém corresponde ao que você procurou.</p>
        )}

        {!loading && !error && visible.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Pessoa</th>
                  <th scope="col">Primeiro acesso</th>
                  <th scope="col">Último acesso</th>
                  <th scope="col">Usou</th>
                  <th scope="col" className="num">
                    Entradas
                  </th>
                  <th scope="col">Grupos</th>
                  <th scope="col" className="col-action">
                    Administrador
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => (
                  <tr key={u.oid}>
                    <th scope="row" className="cell-person">
                      <Avatar
                        src={u.hasPhoto ? photoUrl.user(u.oid) : null}
                        initials={initialsOf(u.displayName, u.upn)}
                        name={u.displayName ?? u.upn}
                        size="sm"
                      />
                      <span className="person-text">
                        <span className="person-name">
                          {u.displayName ?? '—'}
                          {u.oid === me?.oid && <span className="you-tag">você</span>}
                        </span>
                        <span className="person-upn">{u.upn}</span>
                      </span>
                    </th>

                    <td>
                      <span className="cell-main">{dateTime(u.firstSeenAt)}</span>
                    </td>

                    {/* ⚠ Mostra a ATIVIDADE, não o login — e a diferença não
                        é detalhe.

                        Até 26/10 esta célula mostrava `lastSeenAt`, que é o
                        instante do LOGIN. Com sessão de horas, uma pessoa
                        aparecia com "último acesso 09:13" tendo usado a
                        plataforma às 10:26. O dado estava certo; o rótulo
                        "Último acesso" é que prometia outra coisa.

                        O login continua à vista, no `title` e na coluna
                        Entradas — ele responde outra pergunta: com que
                        frequência a pessoa entra. */}
                    <td>
                      <span className="cell-main">
                        {dateTime(u.lastActivityAt ?? u.lastSeenAt)}
                        {u.online && (
                          <span
                            className="ponto-online"
                            title="Deu sinal ao servidor nos últimos minutos"
                          >
                            <span className="ponto" aria-hidden="true" />
                            agora
                          </span>
                        )}
                      </span>
                      <span className="cell-sub" title={`Último login: ${dateTime(u.lastSeenAt)}`}>
                        {relativeDays(u.lastActivityAt ?? u.lastSeenAt)}
                      </span>
                    </td>

                    <td>
                      <UserActivity user={u} items={items} />
                    </td>

                    <td className="num">{u.signInCount}</td>

                    <td>
                      {/* Lê o Entra ao vivo, então só quando alguém pede. Uma
                          consulta por linha ao carregar a lista seria uma ida
                          ao Graph por pessoa, toda vez que a aba abre. */}
                      <button
                        type="button"
                        className="button ghost"
                        onClick={() => setVendoGrupos(u)}
                        title="Ver em que grupos do Entra esta pessoa está, e o que eles liberam"
                      >
                        Ver grupos
                      </button>
                    </td>

                    <td className="col-action">
                      <button
                        type="button"
                        className={`admin-toggle ${u.isAdmin ? 'is-on' : ''}`}
                        disabled={saving === u.oid}
                        aria-pressed={u.isAdmin}
                        onClick={() => toggleAdmin(u)}
                      >
                        {saving === u.oid ? 'Salvando…' : u.isAdmin ? 'Sim' : 'Não'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="panel-ft">
          A lista tem quem já entrou pelo menos uma vez. Quem nunca entrou não aparece — a
          plataforma não lê o diretório da companhia, só registra quem chega.
          <br />
          <strong>Último acesso</strong> é a última vez que a pessoa usou a plataforma, não o último
          login — passe o cursor para ver o login. <strong>agora</strong> significa que ela deu
          sinal nos últimos {online.janelaMin} minutos, com a plataforma aberta na frente dela.
        </div>
      </div>

      {vendoGrupos && <GruposDaPessoa usuario={vendoGrupos} onClose={() => setVendoGrupos(null)} />}
    </>
  );
}

/**
 * O que esta pessoa usou: os itens que ela abriu e aqueles com quem conversou.
 *
 * Duas origens somadas pelo servidor. O `access_log` sabe o que ela **abriu**
 * pela plataforma; a telemetria sabe com o que ela **conversou** — e nem todo
 * item aberto gera conversa (ferramenta externa não manda telemetria), nem
 * toda conversa passou por um clique nosso (dá para abrir o agente direto no
 * Teams).
 *
 * Onde há conversa, o ícone leva às sessões daquela pessoa com aquele agente.
 * Onde não há, ele fica sem link — e o `title` diz por quê, em vez de deixar
 * a pessoa clicando num ícone morto.
 */
function UserActivity({ user, items }) {
  const activity = user.activity ?? [];
  if (activity.length === 0) return <span className="turn-hidden">—</span>;

  const byId = new Map(items.map((i) => [i.id, i]));

  return (
    <span className="user-activity">
      {activity.slice(0, 6).map((a) => {
        const item = byId.get(a.itemId);
        const nome = item?.name ?? a.itemId;
        const mark = (
          <span className="item-mark mark-xs" aria-hidden="true">
            <span className="mark-initials">
              {item?.initials ?? nome.slice(0, 2).toUpperCase()}
            </span>
            {item?.hasLogo && (
              <img className="mark-logo" src={logoUrl(a.itemId, item.logoUpdatedAt)} alt="" />
            )}
          </span>
        );

        if (a.conversations > 0) {
          return (
            <a
              key={a.itemId}
              className="activity-chip"
              href={`/admin?aba=conversations&item=${encodeURIComponent(a.itemId)}&usuario=${encodeURIComponent(user.oid)}`}
              title={`${nome}: ${a.conversations} conversa(s). Ver sessões`}
            >
              {mark}
            </a>
          );
        }

        return (
          <span
            key={a.itemId}
            className="activity-chip is-flat"
            title={`${nome}: aberto ${a.opens} vez(es). Sem registro de conversa`}
          >
            {mark}
          </span>
        );
      })}
      {activity.length > 6 && <span className="activity-more">+{activity.length - 6}</span>}
    </span>
  );
}

/** Mesma regra do servidor: "Ana Paula Souza" → "AS". */
function initialsOf(name, upn) {
  const base = (name ?? upn ?? '').trim();
  if (!base) return '?';
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/* ── catálogo ────────────────────────────────────────────────── */

const STATUS_OPTIONS = [
  { id: 'ACTIVE', label: 'Ativo' },
  { id: 'COMING_SOON', label: 'Em breve' },
  { id: 'OUT_OF_MATRIX', label: 'Fora da matriz' },
  { id: 'INACTIVE', label: 'Inativo (some da tela)' },
];

const STATUS_CLASS = {
  ACTIVE: 'ok',
  COMING_SOON: 'pending',
  OUT_OF_MATRIX: 'neutral',
  INACTIVE: 'neutral',
};

/** Monograma do item, com o logotipo por cima quando existe. */
function ItemMark({ item }) {
  return (
    <span className="item-mark" aria-hidden="true">
      <span className="mark-initials">{item.initials ?? item.name.slice(0, 2).toUpperCase()}</span>
      {item.hasLogo && (
        <img
          className="mark-logo"
          src={logoUrl(item.id, item.logoUpdatedAt)}
          alt=""
          loading="lazy"
        />
      )}
    </span>
  );
}

function CatalogTab() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('ALL');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.admin.catalog();
      setItems(r.items);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setLoading(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    if (!items) return [];
    const term = search.trim().toLowerCase();
    return items.filter((i) => {
      if (kind !== 'ALL' && i.kind !== kind) return false;
      if (!term) return true;
      // Busca também pelo `id`: é por ele que a gente conversa sobre um item
      // em chamado e em migration, e é o que se tem na mão quando algo quebra.
      return (
        i.name.toLowerCase().includes(term) ||
        i.id.toLowerCase().includes(term) ||
        (i.category ?? '').toLowerCase().includes(term) ||
        (i.vendor ?? '').toLowerCase().includes(term)
      );
    });
  }, [items, search, kind]);

  const replaceItem = useCallback((item) => {
    setItems((list) => list.map((i) => (i.id === item.id ? item : i)));
  }, []);

  function onSaved(item) {
    replaceItem(item);
    setEditing(null);
    notify(`"${item.name}" foi atualizado.`);
  }

  return (
    <>
      <StatsBar
        items={[
          { label: 'Itens no catálogo', value: items?.length ?? '—' },
          {
            label: 'Ativos',
            value: items ? items.filter((i) => i.status === 'ACTIVE').length : '—',
          },
        ]}
      />

      <div className="list-panel">
        <div className="tabs-row">
          <div className="tabs" role="tablist" aria-label="Filtrar por tipo">
            {[
              { id: 'ALL', label: 'Todos' },
              { id: 'AGENT', label: 'Agentes' },
              { id: 'SYSTEM', label: 'Sistemas' },
            ].map((o) => (
              <button
                key={o.id}
                type="button"
                role="tab"
                aria-selected={kind === o.id}
                className={`tab ${kind === o.id ? 'is-active' : ''}`}
                onClick={() => setKind(o.id)}
              >
                {o.label}
              </button>
            ))}
          </div>

          <div className="tabs-actions">
            <input
              className="search-box"
              type="search"
              placeholder="Buscar por nome, identificador ou área…"
              aria-label="Buscar no catálogo"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <button type="button" className="button primary" onClick={() => setCreating(true)}>
              Novo item
            </button>
          </div>
        </div>

        {loading && <p className="state-message">Carregando…</p>}

        {error && (
          <div className="state-message error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={load}>
              Tentar de novo
            </button>
          </div>
        )}

        {!loading && !error && visible.length === 0 && (
          <p className="state-message">Nenhum item corresponde ao que você procurou.</p>
        )}

        {!loading && !error && visible.length > 0 && (
          <div className="table-scroll">
            <table className="data-table catalog-table">
              <thead>
                <tr>
                  <th scope="col">Item</th>
                  <th scope="col">Tipo</th>
                  <th scope="col">Área</th>
                  <th scope="col">Estado</th>
                  <th scope="col" className="col-action">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((i) => (
                  <tr key={i.id}>
                    <th scope="row" className="cell-person">
                      <ItemMark item={i} />
                      <span className="person-text">
                        <span className="person-name">{i.name}</span>
                        <span className="person-upn">{i.id}</span>
                      </span>
                    </th>
                    <td>{i.kind === 'AGENT' ? 'Agente' : 'Sistema'}</td>
                    <td>{i.category ?? '—'}</td>
                    <td>
                      <span className={`status-pill ${STATUS_CLASS[i.status] ?? 'neutral'}`}>
                        {STATUS_OPTIONS.find((o) => o.id === i.status)?.label ?? i.status}
                      </span>
                    </td>
                    <td className="col-action">
                      <button
                        type="button"
                        className="button ghost row-action"
                        onClick={() => setEditing(i)}
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="panel-ft">
          O que aparece no portfólio sai daqui: nome, descrição, área, logotipo, endereços e
          etiquetas. Item em <b>Em breve</b> aparece esmaecido; em <b>Inativo</b>, some da tela sem
          perder o histórico.
        </div>
      </div>

      {editing && (
        <ItemEditor
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
          onItemChanged={replaceItem}
        />
      )}

      {creating && (
        <NewItem
          onClose={() => setCreating(false)}
          onCreated={(item) => {
            // Entra na lista já ordenado, em vez de aparecer no fim: a tabela
            // é ordenada por tipo e nome, e um item solto no rodapé pareceria
            // não ter sido salvo.
            setItems((lista) =>
              [...lista, item].sort(
                (a, b) => a.kind.localeCompare(b.kind) || a.name.localeCompare(b.name, 'pt-BR'),
              ),
            );
            setCreating(false);
          }}
        />
      )}
    </>
  );
}

/* ── editor de um item ───────────────────────────────────────── */

/**
 * O formulário é o contêiner que rola, não a `<div>` de dentro.
 *
 * Na primeira versão o `<form>` ficava entre a moldura e o corpo, sem ser
 * `flex`. O `overflow-y: auto` do corpo não tinha altura para respeitar, o
 * formulário crescia além do `max-height` da moldura e **o rodapé com os botões
 * saía da tela** — dava para editar e não dava para salvar.
 */
function ItemEditor({ item, onClose, onSaved, onItemChanged }) {
  const [form, setForm] = useState({
    name: item.name ?? '',
    shortName: item.shortName ?? '',
    description: item.description ?? '',
    category: item.category ?? '',
    accessLabel: item.accessLabel ?? '',
    vendor: item.vendor ?? '',
    platformType: item.platformType ?? '',
    initials: item.initials ?? '',
    status: item.status,
    tags: (item.tags ?? []).join(', '),
    targetUrl: item.targetUrl ?? '',
    ownerName: item.ownerName ?? '',
    ownerEmail: item.ownerEmail ?? '',
    loginHint: item.loginHint ?? '',
    requestBlockedReason: item.requestBlockedReason ?? '',
  });
  // Fora do `form` porque é interruptor, não texto: misturá-lo faria o
  // `set()` genérico ter que distinguir `checked` de `value`.
  const [requestBlocked, setRequestBlocked] = useState(() => Boolean(item.requestBlocked));
  // Fora do `form` pelo mesmo motivo dos links: é um conjunto, não texto.
  const [areas, setAreas] = useState(() => item.areas ?? []);
  // Fora do `form` porque não é campo de texto: é uma lista que cresce e
  // encolhe, e misturá-la ao resto faria cada tecla digitada recriar o array.
  const [links, setLinks] = useState(() =>
    (item.links ?? []).map((l) => ({ label: l.label ?? '', url: l.url ?? '' })),
  );
  const [current, setCurrent] = useState(item);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Fecha com Esc, como o painel de detalhe. Sem isso o único jeito de sair é
  // achar o × com o mouse.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      // Campo de texto vazio vira `null`, não string vazia: no banco a coluna é
      // anulável, e `''` faria a tela mostrar um rótulo em branco em vez de
      // esconder a linha.
      const empty = (v) => (v.trim() === '' ? null : v.trim());

      const r = await api.admin.saveItem(item.id, {
        name: form.name.trim(),
        shortName: empty(form.shortName),
        description: empty(form.description),
        category: empty(form.category),
        areas,
        accessLabel: empty(form.accessLabel),
        vendor: empty(form.vendor),
        platformType: empty(form.platformType),
        initials: empty(form.initials),
        status: form.status,
        tags: form.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        targetUrl: form.targetUrl.trim(),
        ownerName: empty(form.ownerName),
        ownerEmail: form.ownerEmail.trim(),
        loginHint: empty(form.loginHint),
        requestBlocked,
        // Só manda o motivo quando o bloqueio está ligado: a rota o exige
        // nesse caso, e mandar texto solto com o bloqueio desligado
        // gravaria um porquê que ninguém vai ler.
        requestBlockedReason: requestBlocked ? form.requestBlockedReason.trim() : null,
        // Linha pela metade é descartada em silêncio: quem começou a
        // preencher e desistiu não deveria ver um erro de validação por isso.
        links: links
          .map((l) => ({ label: l.label.trim(), url: l.url.trim() }))
          .filter((l) => l.label && l.url),
      });
      onSaved(r.item);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setSaving(false);
    }
    return undefined;
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      {/* O clique de dentro não pode fechar: sem isto, selecionar texto num
          campo e soltar o botão fora fecharia tudo e perderia o que foi
          digitado. */}
      <div
        className="item-panel editor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="editor-title"
        onClick={(e) => e.stopPropagation()}
      >
        <form className="editor-form" onSubmit={submit}>
          <header className="panel-header editor-head">
            <h2 id="editor-title" className="panel-title">
              {item.name}
            </h2>
            <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
              ×
            </button>
          </header>

          <div className="panel-body editor-body">
            {/* O envio do logotipo grava na hora, sem esperar o `Salvar`. A
                lista de tras precisa saber: sem isto, fechar sem salvar
                deixaria a tela mostrando a imagem antiga, e pareceria que o
                envio nao funcionou. */}
            <LogoField
              item={current}
              onChange={(novo) => {
                setCurrent(novo);
                onItemChanged?.(novo);
              }}
            />

            <Field label="Nome" htmlFor="f-name">
              <input
                id="f-name"
                className="input"
                value={form.name}
                onChange={set('name')}
                required
                maxLength={200}
              />
            </Field>

            <div className="field-row">
              <Field label="Nome curto" htmlFor="f-short" hint="Usado no botão. Ex.: “Copilot”">
                <input
                  id="f-short"
                  className="input"
                  value={form.shortName}
                  onChange={set('shortName')}
                  maxLength={60}
                />
              </Field>
              <Field label="Iniciais" htmlFor="f-init" hint="Até 4 letras, sem logotipo">
                <input
                  id="f-init"
                  className="input"
                  value={form.initials}
                  onChange={set('initials')}
                  maxLength={4}
                />
              </Field>
            </div>

            <Field
              label="Descrição"
              htmlFor="f-desc"
              hint={`Aparece no cartão e no detalhe · ${form.description.length}/4000`}
            >
              <textarea
                id="f-desc"
                className="textarea"
                rows={8}
                value={form.description}
                onChange={set('description')}
                maxLength={4000}
              />
            </Field>

            <Field
              label="Aparece nas abas"
              hint="É o que decide em que abas do portfólio o item se mostra. Sem nenhuma, ele some de todas"
            >
              <AreaPicker value={areas} onChange={setAreas} />
            </Field>

            <div className="field-row">
              <Field
                label="Área responsável"
                htmlFor="f-cat"
                hint="Só informativo, aparece no detalhe. Não muda as abas acima"
              >
                <input
                  id="f-cat"
                  className="input"
                  value={form.category}
                  onChange={set('category')}
                  maxLength={80}
                />
              </Field>
              <Field label="Fornecedor" htmlFor="f-vendor">
                <input
                  id="f-vendor"
                  className="input"
                  value={form.vendor}
                  onChange={set('vendor')}
                  maxLength={120}
                />
              </Field>
            </div>

            {/* O responsável decide para quem o pedido é endereçado e quem
                recebe a notificação. Não concede acesso nenhum — por isso
                mora aqui, e não na aba Grupos. */}
            <div className="field-row">
              <Field label="Responsável" htmlFor="f-owner-name">
                <input
                  id="f-owner-name"
                  className="input"
                  value={form.ownerName}
                  onChange={set('ownerName')}
                  maxLength={200}
                  placeholder="Nome de exibição"
                />
              </Field>
              <Field
                label="E-mail do responsável"
                htmlFor="f-owner-email"
                hint="É quem aprova o pedido e recebe o aviso"
              >
                <input
                  id="f-owner-email"
                  className="input"
                  type="email"
                  value={form.ownerEmail}
                  onChange={set('ownerEmail')}
                  maxLength={200}
                  required
                />
              </Field>
            </div>

            <Field
              label="Público"
              htmlFor="f-access"
              hint="Descritivo. Quem autoriza de fato é o grupo do Entra"
            >
              <input
                id="f-access"
                className="input"
                value={form.accessLabel}
                onChange={set('accessLabel')}
                maxLength={200}
              />
            </Field>

            <Field label="Plataforma" htmlFor="f-plat">
              <input
                id="f-plat"
                className="input"
                value={form.platformType}
                onChange={set('platformType')}
                maxLength={120}
              />
            </Field>

            <Field
              label="Etiquetas"
              htmlFor="f-tags"
              hint="Separadas por vírgula. Aparecem no detalhe e entram na busca do catálogo"
            >
              <input id="f-tags" className="input" value={form.tags} onChange={set('tags')} />
            </Field>

            {item.accessPattern === 'C' && (
              <Field
                label="Instrução de login"
                htmlFor="f-hint"
                hint="Aparece no painel. Ex.: “Entre com o usuário do Looqbox, não com a conta Microsoft”"
              >
                <input
                  id="f-hint"
                  className="input"
                  value={form.loginHint}
                  onChange={set('loginHint')}
                  maxLength={500}
                />
              </Field>
            )}

            {/* ## Bloqueio de solicitação
                Para ferramenta de uso fechado: existe no portfólio, aparece
                para todos, e não se pede acesso a ela. O motivo é obrigatório
                porque "não é possível solicitar", sem porquê, deixa quem lê
                sem saída — ou desiste, ou vai procurar alguém.
                ⚠ Fecha o PEDIDO, não o uso: quem já tem acesso continua
                abrindo. Fechar de verdade é assunto do padrão de acesso. */}
            <Field label="Solicitação de acesso" htmlFor="f-bloqueio">
              <label className="check-linha">
                <input
                  id="f-bloqueio"
                  type="checkbox"
                  checked={requestBlocked}
                  onChange={(e) => setRequestBlocked(e.target.checked)}
                />
                <span>Não aceita solicitação (ferramenta de uso fechado)</span>
              </label>
            </Field>

            {requestBlocked && (
              <Field
                label="Por que não pode ser solicitada"
                htmlFor="f-bloqueio-motivo"
                hint="Aparece no lugar do botão. Ex.: “Uso restrito à Controladoria — procure o time responsável.”"
              >
                <input
                  id="f-bloqueio-motivo"
                  className="input"
                  value={form.requestBlockedReason}
                  onChange={set('requestBlockedReason')}
                  maxLength={400}
                />
              </Field>
            )}

            <Field label="Estado" htmlFor="f-status">
              <select id="f-status" className="input" value={form.status} onChange={set('status')}>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Endereço principal"
              htmlFor="f-url"
              hint="Para onde o botão Abrir leva. Precisa começar com https://"
            >
              <input
                id="f-url"
                className="input"
                type="url"
                value={form.targetUrl}
                onChange={set('targetUrl')}
                maxLength={500}
                placeholder="https://…"
              />
            </Field>

            <LinkList links={links} onChange={setLinks} />

            {error && (
              <p className="error-note" role="alert">
                {error}
              </p>
            )}
          </div>

          <footer className="panel-actions">
            <button type="button" className="button ghost" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="button primary" disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

/**
 * Lista de endereços alternativos do mesmo item.
 *
 * O agente no Teams, o mesmo agente no Microsoft 365, o mesmo agente no
 * Snowflake — três caminhos para a mesma coisa, e até 01/09 só cabia um.
 *
 * Cada linha vira um botão secundário no painel de detalhe, ao lado do
 * principal. Linha pela metade é descartada no envio: quem começou a preencher
 * e desistiu não deve receber erro de validação por isso.
 */
function LinkList({ links, onChange }) {
  const set = (i, campo) => (e) =>
    onChange(links.map((l, n) => (n === i ? { ...l, [campo]: e.target.value } : l)));

  return (
    <div className="field">
      <span className="field-label">
        Outros endereços
        <span className="field-hint">
          O mesmo item em outro lugar — no Teams, no Microsoft 365, no Snowflake. Até 8
        </span>
      </span>

      {links.map((l, i) => (
        // O índice como chave é seguro aqui porque a lista não é reordenada
        // nem filtrada: só cresce no fim e encolhe pela remoção explícita.
        <div className="link-row" key={i}>
          <input
            className="input link-label"
            value={l.label}
            onChange={set(i, 'label')}
            maxLength={60}
            placeholder="Abrir no Teams"
            aria-label={`Rótulo do endereço ${i + 1}`}
          />
          <input
            className="input"
            type="url"
            value={l.url}
            onChange={set(i, 'url')}
            maxLength={500}
            placeholder="https://…"
            aria-label={`Endereço ${i + 1}`}
          />
          <button
            type="button"
            className="button ghost link-remove"
            onClick={() => onChange(links.filter((_, n) => n !== i))}
            aria-label={`Remover o endereço ${i + 1}`}
          >
            ×
          </button>
        </div>
      ))}

      {links.length < 8 && (
        <button
          type="button"
          className="button ghost add-link"
          onClick={() => onChange([...links, { label: '', url: '' }])}
        >
          + Acrescentar endereço
        </button>
      )}
    </div>
  );
}

/**
 * Envio do logotipo.
 *
 * Fica no topo do formulário porque é a mudança mais visível de todas — quem
 * abre "Editar" para trocar a imagem não deveria ter de rolar até o fim.
 *
 * O envio é imediato, e não junto do `Salvar`: o arquivo vai como corpo cru
 * numa rota própria, e misturá-lo ao PATCH de JSON exigiria multipart e uma
 * dependência a mais para um único campo.
 */
function LogoField({ item, onChange }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function upload(file) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.admin.uploadLogo(item.id, file);
      // O item devolvido traz `logoUpdatedAt` novo, e é ele que muda a URL da
      // imagem em toda a aplicação. Um contador local só consertaria esta aba.
      onChange(r.item);
      notify('Logotipo atualizado.');
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setBusy(false);
      // Sem isto, escolher o mesmo arquivo de novo não dispara `change` — e a
      // pessoa acha que o botão parou de funcionar.
      if (inputRef.current) inputRef.current.value = '';
    }
    return undefined;
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.admin.deleteLogo(item.id);
      onChange(r.item);
      notify('Logotipo removido. O item volta a mostrar as iniciais.');
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setBusy(false);
    }
    return undefined;
  }

  return (
    <div className="logo-field">
      <span className="item-mark mark-lg" aria-hidden="true">
        <span className="mark-initials">
          {item.initials ?? item.name.slice(0, 2).toUpperCase()}
        </span>
        {item.hasLogo && (
          <img className="mark-logo" src={logoUrl(item.id, item.logoUpdatedAt)} alt="" />
        )}
      </span>

      <div className="logo-actions">
        <span className="field-label">
          Logotipo
          <span className="field-hint">PNG, JPEG, WEBP ou GIF, até 1 MB</span>
        </span>

        <div className="logo-buttons">
          {/* O `<input type="file">` fica escondido e o `<label>` é o botão:
              o campo nativo não é estilizável e destoaria do resto. */}
          <label className="button secondary file-button" htmlFor="f-logo">
            {item.hasLogo ? 'Trocar imagem' : 'Enviar imagem'}
          </label>
          <input
            id="f-logo"
            ref={inputRef}
            className="visually-hidden"
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            disabled={busy}
            onChange={(e) => upload(e.target.files?.[0])}
          />

          {item.hasLogo && (
            <button type="button" className="button ghost" onClick={remove} disabled={busy}>
              Remover
            </button>
          )}
        </div>

        {busy && <span className="field-hint">Enviando…</span>}
        {error && (
          <p className="error-note" role="alert">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function Field({ label, htmlFor, hint, children }) {
  // Sem `htmlFor` o rótulo não é `<label>`: um `<label for>` vazio não aponta
  // para nada, e clicar nele não faz o que a pessoa espera. É o caso do
  // seletor de abas, que é um grupo de botões e carrega o próprio
  // `aria-label` — mesmo padrão do seletor de tipo em `FeedbackDialog`.
  const Rotulo = htmlFor ? 'label' : 'span';
  return (
    <div className="field">
      <Rotulo className="field-label" htmlFor={htmlFor}>
        {label}
        {hint && <span className="field-hint">{hint}</span>}
      </Rotulo>
      {children}
    </div>
  );
}

/* ── página ──────────────────────────────────────────────────── */

export function Admin() {
  usePageMeta('Administração · AI Hub Iguatemi');

  const [me, setMe] = useState(null);
  const [denied, setDenied] = useState(false);

  // A aba e o filtro inicial saem da URL: é o que faz o "200 conversas" do
  // painel do agente e o ícone da lista de usuários caírem já filtrados aqui.
  //
  // ## Desde 21/10 a URL é a FONTE DA VERDADE, e não só o valor inicial
  //
  // Antes a aba era estado, semeado da URL uma vez. Os dois andavam juntos até
  // o primeiro clique e depois divergiam em silêncio — recarregar devolvia a
  // pessoa para outra aba, e link para uma aba específica só funcionava em
  // janela nova.
  //
  // O tour guiado é quem obrigou a arrumar: ele atravessa as abas navegando, e
  // navegar não mexia em estado nenhum. O ganho é geral — agora dá para mandar
  // a alguém um link de `/admin?aba=grupos`.
  //
  // `replace` e não `push`: trocar de aba não é navegar, e encher o histórico
  // faria o botão Voltar percorrer as oito antes de sair da administração.
  const [params, setParams] = useSearchParams();
  const naUrl = params.get('aba');
  const tab = TABS.some((t) => t.id === naUrl) ? naUrl : 'users';

  const setTab = useCallback(
    (id) =>
      setParams(
        (atual) => {
          atual.set('aba', id);
          return atual;
        },
        { replace: true },
      ),
    [setParams],
  );

  // Só para os filtros e o seletor de vínculo da aba de conversas. Carregada
  // uma vez, aqui, em vez de uma vez por componente que precisa dela.
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((d) => {
        if (!alive) return;
        setMe(d);
        // A recusa de verdade vem do servidor, em cada chamada de `/api/admin`.
        // Este `isAdmin` só evita mostrar a tela de dados e depois trocá-la por
        // um erro — poupa o piscar, não é o controle.
        if (!d.isAdmin) setDenied(true);
      })
      .catch((err) => {
        if (err instanceof SessionError) goToSignIn();
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (denied) return undefined;
    let alive = true;
    api.admin
      .catalog()
      .then((d) => alive && setItems(d.items))
      // Falha aqui deixa os filtros sem a lista de agentes, e nada mais. Não
      // vale derrubar a tela por causa disso.
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [denied]);

  return (
    <AppShell>
      <Banner title="Administração">Quem usa a plataforma e o que aparece no catálogo.</Banner>

      {denied ? (
        <Panel title="Área restrita">
          <p className="state-message">
            Esta área é só para administradores da plataforma. Se você deveria ter acesso, peça a um
            administrador — ou fale com a equipe de Inovação e IA.
          </p>
        </Panel>
      ) : (
        <>
          <div className="admin-tabs">
            <div
              className="tabs"
              role="tablist"
              aria-label="Seções da administração"
              data-tour="admin-abas"
            >
              {TABS.map((t) => (
                <Fragment key={t.id}>
                  {t.separa && <span className="admin-tab-sep" aria-hidden="true" />}
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    className={`tab tab-com-icone ${tab === t.id ? 'is-active' : ''}`}
                    onClick={() => setTab(t.id)}
                  >
                    <Icon name={t.icon} />
                    {t.label}
                  </button>
                </Fragment>
              ))}
            </div>
          </div>

          {tab === 'users' && <UsersTab me={me} items={items} />}
          {/* `me` entra para a tela espelhar a guarda do servidor: ninguém
              decide a própria solicitação, e oferecer um botão que sempre
              falha é pior do que não oferecer. */}
          {tab === 'requests' && <AdminRequests me={me} />}
          {tab === 'access' && <AdminAccess />}
          {tab === 'feedback' && <AdminFeedback initialId={params.get('mensagem')} />}
          {tab === 'catalog' && <CatalogTab />}
          {tab === 'grupos' && <AdminGrupos />}
          {tab === 'sites' && <AdminSites />}
          {tab === 'abas' && <AdminAbas />}
          {tab === 'conversations' && (
            <AdminConversations
              items={items}
              initialItemId={params.get('item') ?? ''}
              initialUserOid={params.get('usuario') ?? ''}
            />
          )}
        </>
      )}

      {/* Faltava, e ninguém tinha percebido: o painel inteiro chama `notify` —
          conceder acesso, retirar, salvar item, responder mensagem, decidir
          solicitação — e **nada aparecia**, porque o `Toast` só estava montado
          no catálogo e na lista de solicitações. Em 14/10 isso levou a clicar
          três vezes em Aprovar achando que o botão não funcionava; a primeira
          tinha funcionado, e a confirmação foi para o vazio. */}
      <Toast />
    </AppShell>
  );
}

export default Admin;
